// /compare/client-gallery-platforms: Pixieset vs Pic-Time vs ShootProof (and Pixally).
//
// Read the header of honeybook.js first, and the list rules in
// honeybookAlternatives.js. Every other product's fact, with source and exact
// words: docs/geo/compare-gallery-platforms-evidence.md.
//
// Prints: Pixally sells prints and albums from the gallery, shipped to the US and
// Canada only (Ryan, 2 Oct 2026: on from launch; the /preview gallery section
// says the same). Which plans include it is not settled, so no page names a plan
// for it. The competitor's store stays on the page as its own edge.

export const ROUTE = '/compare/client-gallery-platforms';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 2, 2026';
export const CHECKED_LABEL = 'Other products checked';
export const HERO_ART = 'options';

export const TITLE = 'Pixieset vs Pic-Time vs ShootProof';
export const SEO_TITLE = 'Pixieset vs Pic-Time vs ShootProof for Wedding Photographers (2026)';
export const SEO_DESCRIPTION =
  'Pixieset, Pic-Time and ShootProof compared for wedding photographers and videographers: price, photo storage, video, print sales and CRM tools, with Pixally alongside.';

export const LEDE =
  'Pixieset, Pic-Time and ShootProof are client gallery platforms: they deliver photos, sell prints and products, and each has a free plan. Pixieset and ShootProof add contracts and invoices; Pic-Time connects to a separate CRM. Pixally, launching on January 1, 2027, comes from the other direction: a CRM for wedding photographers and videographers with photo, film and hybrid galleries and print sales built in.';

const PIXIESET_GALLERY = 'https://pixieset.com/pricing/';
const PIXIESET_SUITE = 'https://pixieset.com/pricing-suite/';
const PICTIME_PRICING = 'https://www.pic-time.com/pricing/client-delivery-suite';
const PICTIME_VIDEO = 'https://www.pic-time.com/pricing/video-storage';
const SHOOTPROOF_PLANS = 'https://www.shootproof.com/plans/';

export const BODY = [
  {
    type: 'paragraph',
    text: `Pixally is our product, so we have put it on this list and tried to treat the others as fairly as we would want to be treated. Every fact about another product comes from that product's own website, checked ${CHECKED_AT}, and the sources are at the bottom of the page.`,
  },
  { type: 'heading', level: 2, text: 'At a glance' },
  {
    type: 'table',
    label: 'Client gallery platforms compared',
    head: ['', 'Price, billed yearly', 'Photo storage', 'Video', 'CRM tools'],
    rows: [
      ['Pixieset', 'Free plan; Client Gallery $8 to $40 a month; Suite (galleries, CRM, website) $28 to $55', 'Client Gallery 3 GB free to unlimited; Suite 100 GB to unlimited', 'Client Gallery 30 minutes to 5 hours; Suite 1 to 10 hours; $5 a month per extra hour', 'Studio Manager: invoices, contracts, questionnaires and booking, free or in the Suite'],
      ['Pic-Time', 'Free plan; $7, $21 or $42 a month', '10 GB, 20 GB, 100 GB or unlimited', '1 GB to 60 GB by plan (about 6 hours at most); more from $10 a month for 100 GB', 'None built in; connects to HoneyBook, Studio Ninja, Iris Works, Light Blue and Pixifi'],
      ['ShootProof', 'Free plan; $8.33, $16.67, $26.67 or $50 a month', '3 GB free; 20 GB or 100 GB shared with video; 1,000 GB; unlimited', 'Shared with photos on Starter and Standard; 50 GB on Professional; 150 GB on Premium', 'Contracts and invoices on paid plans; booking add-on $6.99 a month'],
      ['Pixally', 'Free plan; $33, $58 or $99 a month', 'None on Free; 200 GB, 1 TB or unlimited', '50 GB to start on paid plans, then packs from $50 for 50 GB; no limit on film length', 'A full CRM: leads, proposals, contracts, payments, projects, post-production and second shooters'],
    ],
  },
  { type: 'heading', level: 2, text: 'The platforms' },
  { type: 'heading', level: 3, text: 'Pixieset' },
  {
    type: 'paragraph',
    text: `[Pixieset Client Gallery](${PIXIESET_GALLERY}) starts free with 3 GB, then runs from $8 to $40 a month billed yearly, with 10 GB to unlimited photo storage and 30 minutes to 5 hours of video; extra video is $5 a month per hour. Its store is commission-free on paid plans. [The Pixieset Suite](${PIXIESET_SUITE}) adds the Studio Manager CRM and a website for $28, $38 or $55 a month billed yearly, with 1, 4 or 10 hours of video.`,
  },
  { type: 'heading', level: 3, text: 'Pic-Time' },
  {
    type: 'paragraph',
    text: `[Pic-Time](${PICTIME_PRICING}) is built around galleries and sales: a print store with more than 30 labs, an album designer, slideshows with music, Lightroom uploads, and face and selfie search for guests. Plans are free, $7, $21 or $42 a month billed yearly, with 10 GB to unlimited photo storage and 1 GB to 60 GB of video, up to 4K. [More video storage](${PICTIME_VIDEO}) is $10 a month for 100 GB or $25 for 1 TB. It has no CRM of its own and connects to HoneyBook, Studio Ninja, Iris Works, Light Blue and Pixifi.`,
  },
  { type: 'heading', level: 3, text: 'ShootProof' },
  {
    type: 'paragraph',
    text: `[ShootProof](${SHOOTPROOF_PLANS}) puts galleries, contracts, invoices, selling, slideshows and video in every paid plan, and the plans differ only in storage: $8.33, $16.67, $26.67 or $50 a month billed yearly, from 20 GB shared with video up to unlimited photos with 150 GB of video. Sales are commission-free, and online booking is an add-on at $6.99 a month. The trial is 14 days.`,
  },
  { type: 'heading', level: 3, text: 'Pixally (ours, launching January 1, 2027)' },
  {
    type: 'paragraph',
    text: 'Pixally is a CRM first, built for wedding photographers and videographers, with photo, film and hybrid galleries in the same project as the booking. Films have no length limit and play with chapters, and video delivery comes in packs that never expire rather than a monthly storage tier. Couples can order prints and albums from the gallery, shipped to the United States and Canada. [How Pixally and HoneyBook compare](/compare/honeybook) covers the CRM side in detail.',
  },
  { type: 'heading', level: 2, text: 'When a gallery platform is the better choice' },
  {
    type: 'list',
    items: [
      'Print sales are central to your business and you want more choice of labs and products. All three have built-in stores with lab fulfillment; Pixally\'s prints and albums ship only to the US and Canada.',
      'You want a free or very cheap gallery today: Pic-Time from $7 and ShootProof from $8.33 a month billed yearly, and all three have free plans.',
      'You already have a CRM you like. Pic-Time connects to HoneyBook, Studio Ninja and others.',
      'You want face or selfie search for guests, which Pic-Time offers.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you deliver a lot of film, run second shooters and editors, or want your bookings, contracts, payments, edit and delivery in one system instead of a gallery platform plus a CRM.',
  },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: "The gallery platforms cap video by plan: Pic-Time at 60 GB and ShootProof at 150 GB before add-ons, Pixieset's Suite at 10 hours. Pixally puts no limit on film length, and its packs (50 GB for $50, 100 GB for $95, 500 GB for $450) never expire.",
  },
];

export const FAQ = [
  {
    q: 'Which is the best client gallery platform for wedding photographers?',
    a: 'It depends on what you sell and what else you use. Pic-Time is built around print sales, albums and guest features. ShootProof includes contracts and invoices in every paid plan and differs only by storage. Pixieset bundles galleries, a CRM and a website in its Suite. Pixally, from January 1, 2027, is for studios that want the CRM, film delivery and their crew in one place.',
  },
  {
    q: 'Pixieset vs Pic-Time vs ShootProof: which handles video best?',
    a: "By plan, Pixieset's Suite includes up to 10 hours of video, ShootProof up to 150 GB and Pic-Time up to 60 GB, each with paid add-ons for more. Pixally has no film-length limit and sells video in packs that never expire.",
  },
  {
    q: 'Do gallery platforms include a CRM?',
    a: 'Partly. Pixieset has Studio Manager, free or in its Suite, and ShootProof includes contracts and invoices with booking as an add-on. Pic-Time has no CRM of its own and connects to one.',
  },
  {
    q: 'Which is cheapest?',
    a: 'All three have free plans. Among paid plans billed yearly, Pic-Time starts at $7 a month, Pixieset Client Gallery at $8 and ShootProof at $8.33.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'Pixieset Client Gallery pricing', url: PIXIESET_GALLERY },
  { label: 'Pixieset Suite pricing', url: PIXIESET_SUITE },
  { label: 'Pic-Time pricing', url: PICTIME_PRICING },
  { label: 'Pic-Time video storage pricing', url: PICTIME_VIDEO },
  { label: 'ShootProof plans', url: SHOOTPROOF_PLANS },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
];
